import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle, faClock } from '@fortawesome/free-solid-svg-icons'
import api from '@/services/api'
import { useTranslation } from 'react-i18next'
import { UserListTable, UserActionModal } from '@/components/User'
import type { ColumnConfig, FilterConfig, PaginationState, UserAction, Organizer } from '@/components/User'
import PageHeader from '@/components/PageHeader'
import ErrorDisplay from '@/components/ErrorDisplay'

/**
 * Organizers Management Page (Refactored with reusable components)
 * Dark mode geek aesthetic
 */
export default function OrganizersPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [organizers, setOrganizers] = useState<Organizer[]>([])
  const [filteredOrganizers, setFilteredOrganizers] = useState<Organizer[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Pagination state
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    pageSize: 25,
    total: 0,
  })

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'suspended'>('all')

  // Modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean
    action: UserAction | null
    selectedOrganizer: Organizer | null
    isLoading: boolean
  }>({
    isOpen: false,
    action: null,
    selectedOrganizer: null,
    isLoading: false,
  })

  // Fetch organizers on mount
  useEffect(() => {
    const fetchOrganizers = async () => {
      try {
        const response = await api.get('/superadmin/users?role=organizer')
        if (response.data.success && response.data.data?.users) {
          setOrganizers(response.data.data.users)
          setPagination((prev) => ({
            ...prev,
            total: response.data.data.users.length,
          }))
        }
      } catch (err: any) {
        setError(err.response?.data?.error || t('common.error'))
      } finally {
        setLoading(false)
      }
    }

    fetchOrganizers()
  }, [t])

  // Filter organizers based on search and status
  useEffect(() => {
    let filtered = organizers

    // Apply search filter
    if (searchTerm) {
      const lower = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (org) =>
          org.email?.toLowerCase().includes(lower) ||
          (org.profile?.firstName?.toLowerCase().includes(lower) || false) ||
          (org.profile?.lastName?.toLowerCase().includes(lower) || false) ||
          (org.profile?.organization?.toLowerCase().includes(lower) || false)
      )
    }

    // Apply status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter((org) => !org.isSuspended)
    } else if (statusFilter === 'suspended') {
      filtered = filtered.filter((org) => org.isSuspended)
    }

    setFilteredOrganizers(filtered)
    setPagination((prev) => ({
      ...prev,
      total: filtered.length,
      page: 1, // Reset to first page on filter change
    }))
  }, [organizers, searchTerm, statusFilter])

  // Get paginated data
  const paginatedOrganizers = filteredOrganizers.slice(
    (pagination.page - 1) * pagination.pageSize,
    pagination.page * pagination.pageSize
  )

  // Handle row click (navigate to details)
  const handleRowClick = (organizer: any) => {
    navigate(`/organizers/${organizer.id}`)
  }

  // Handle action modal open
  const handleAction = (organizerId: string, action: UserAction) => {
    const organizer = organizers.find((o) => o.id === organizerId)
    if (!organizer) return

    setModalState({
      isOpen: true,
      action,
      selectedOrganizer: organizer,
      isLoading: false,
    })
  }

  // Handle action confirm
  const handleConfirmAction = async () => {
    if (!modalState.selectedOrganizer || !modalState.action) return

    setModalState((prev) => ({ ...prev, isLoading: true }))

    try {
      const organizerId = modalState.selectedOrganizer.id

      switch (modalState.action) {
        case 'suspend':
          await api.put(`/superadmin/users/${organizerId}/status`, { status: 'suspended' })
          setOrganizers((prev) =>
            prev.map((org) =>
              org.id === organizerId ? { ...org, isSuspended: true } : org
            )
          )
          break

        case 'unsuspend':
          await api.put(`/superadmin/users/${organizerId}/status`, { status: 'active' })
          setOrganizers((prev) =>
            prev.map((org) =>
              org.id === organizerId ? { ...org, isSuspended: false } : org
            )
          )
          break

        case 'delete':
          await api.delete(`/superadmin/users/${organizerId}`)
          setOrganizers((prev) => prev.filter((org) => org.id !== organizerId))
          break

        case 'reset-password':
          await api.post(`/superadmin/users/${organizerId}/reset-password`)
          break

        default:
          break
      }

      setModalState({
        isOpen: false,
        action: null,
        selectedOrganizer: null,
        isLoading: false,
      })
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.error'))
      setModalState((prev) => ({ ...prev, isLoading: false }))
    }
  }

  // Helper function to get row background color based on subscription plan
  const getRowClassName = (org: Organizer): string => {
    const baseClasses = 'hover:bg-slate-800/50 transition group cursor-pointer'
    const planType = org.subscription?.type || 'free'
    
    // Color variations based on plan (more visible but still subtle)
    switch (planType) {
      case 'pro':
        return `${baseClasses} bg-blue-950/20 border-l-2 border-l-blue-500/50`
      case 'premium':
        return `${baseClasses} bg-purple-950/20 border-l-2 border-l-purple-500/50`
      case 'ultimate':
        return `${baseClasses} bg-amber-950/20 border-l-2 border-l-amber-500/50`
      case 'free':
      default:
        return baseClasses
    }
  }

  // Column configuration
  const columns: ColumnConfig[] = [
    {
      key: 'statusIcons',
      label: '',
      width: '60px',
      render: (_, org) => {
        const status = org.subscription?.status
        const expiresAt = org.subscription?.subscriptionExpiresAt
        
        // Show cancellation icon if subscription is canceled and has expiration date
        if (status === 'canceled' && expiresAt) {
          const expirationDate = new Date(expiresAt)
          const now = new Date()
          const isExpired = expirationDate < now
          
          return (
            <div className="flex items-center justify-center w-full">
              <FontAwesomeIcon
                icon={isExpired ? faExclamationTriangle : faClock}
                className={`text-base ${
                  isExpired
                    ? 'text-red-400'
                    : 'text-orange-400'
                }`}
                title={
                  isExpired
                    ? `Abonnement expiré le ${expirationDate.toLocaleDateString('fr-FR')}`
                    : `Annulation programmée le ${expirationDate.toLocaleDateString('fr-FR')}`
                }
              />
            </div>
          )
        }
        
        // Always return a div to maintain column structure
        return <div className="w-full h-5" /> // Empty space to maintain alignment
      },
    },
    {
      key: 'profile.firstName',
      label: t('common.name'),
      sortable: true,
      render: (_, org) => `${org.profile?.firstName || '—'} ${org.profile?.lastName || '—'}`,
    },
    {
      key: 'email',
      label: t('common.email'),
      sortable: true,
    },
    {
      key: 'organization',
      label: t('superadmin.company'),
      render: (_, org) => org.profile?.organization || '—',
    },
    {
      key: 'eventsCount',
      label: t('superadmin.events'),
      sortable: true,
      render: (value) => value || 0,
    },
    {
      key: 'totalRevenue',
      label: 'Revenus',
      sortable: true,
      render: (value) => {
        if (value === null || value === undefined || isNaN(value)) {
          return '€0,00'
        }
        return new Intl.NumberFormat('fr-FR', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value)
      },
    },
    {
      key: 'subscription.type',
      label: 'Plan',
      render: (_, org) => {
        // console.log('value', org.subscription.type)
        if (org.subscription.type === 'free' || !org.subscription.type) return 'Gratuit'
        return org.subscription.type
      },
    },
    {
      key: 'createdAt',
      label: t('common.joinedDate'),
      sortable: true,
      render: (value) => new Date(value).toLocaleDateString('fr-FR'),
    },
    {
      key: 'isSuspended',
      label: t('common.status'),
      render: (value) => (
        <span
          className={`px-3 py-1 rounded-full text-xs font-bold ${
            value
              ? 'bg-red-900/50 text-red-300 border border-red-700/50'
              : 'bg-green-900/50 text-green-300 border border-green-700/50'
          }`}
        >
          {value ? t('common.suspended') : t('common.active')}
        </span>
      ),
    },
  ]

  // Filter configuration
  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: t('common.status'),
      type: 'select',
      options: [
        { value: 'all', label: t('common.all') },
        { value: 'active', label: t('common.active') },
        { value: 'suspended', label: t('common.suspended') },
      ],
    },
  ]
// console.log('pageinateOrganizers', paginatedOrganizers)
  return (
    <div className="space-y-6">
      <PageHeader
        title={t('superadmin.organizers')}
        subtitle="list"
        count={filteredOrganizers.length}
        subtitleColor="purple"
      />

      <ErrorDisplay error={error} />

      {/* User List Table */}
      <UserListTable
        users={paginatedOrganizers}
        columns={columns}
        loading={loading}
        error={error}
        searchPlaceholder="Email, nom, organisation..."
        filters={filters}
        pagination={pagination}
        onSearch={(term) => setSearchTerm(term)}
        onFilterChange={(filters) => {
          setStatusFilter((filters.status as 'all' | 'active' | 'suspended') || 'all')
        }}
        onPaginationChange={(page, pageSize) => {
          setPagination((prev) => ({
            ...prev,
            page,
            pageSize,
          }))
        }}
        onRowClick={handleRowClick}
        onAction={handleAction}
        userType="organizer"
        emptyMessage="Aucun organisateur trouvé"
        getRowClassName={getRowClassName}
      />

      {/* Action Modal */}
      <UserActionModal
        isOpen={modalState.isOpen}
        action={modalState.action}
        userType="organizer"
        userName={
          modalState.selectedOrganizer
            ? `${modalState.selectedOrganizer.profile?.firstName} ${modalState.selectedOrganizer.profile?.lastName}`
            : ''
        }
        isLoading={modalState.isLoading}
        onConfirm={handleConfirmAction}
        onCancel={() =>
          setModalState({
            isOpen: false,
            action: null,
            selectedOrganizer: null,
            isLoading: false,
          })
        }
      />
    </div>
  )
}
